import { format } from "date-fns"
import { ru } from "date-fns/locale"
import { useCalendarEventStore } from "@/store/calendarEventStore"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Calendar, ListChecks, Users, AlertTriangle } from "lucide-react"

export function TodayDashboard() {
  const { events } = useCalendarEventStore()

  const today = new Date()
  const todayStr = format(today, "yyyy-MM-dd")

  const todayEvents = events.filter((ev) => ev.date === todayStr)

  const holidayEvents = todayEvents.filter((ev) =>
    ev.type === "holiday" ||
    ev.type === "leader_birthday" ||
    ev.type === "leader_appointment" ||
    ev.type === "branch_opening"
  );

  const taskEvents = todayEvents.filter((ev) => ev.type === "task");
  const meetingEvents = todayEvents.filter((ev) => ev.type === "meeting");
  const emergencyEvents = todayEvents.filter((ev) => ev.type === "local-emergency");

  return (
    <Card className="bg-gradient-to-br from-purple-950/40 to-indigo-950/40 border-purple-700/50 shadow-2xl backdrop-blur-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
          <Calendar className="h-7 w-7 text-purple-400" />
          Сегодня — {format(today, "d MMMM yyyy", { locale: ru })}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          
          {/* Задачи */}
          <div className="bg-gradient-to-br from-red-950/80 to-rose-950/80 border border-red-700/50 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <ListChecks className="h-6 w-6 text-red-400" />
              <h3 className="text-lg font-semibold text-red-200">
                Задачи ({taskEvents.length})
              </h3>
            </div>
            {taskEvents.length === 0 ? (
              <p className="text-red-300/80 text-center py-6">Нет задач на сегодня</p>
            ) : (
              <ul className="space-y-3 max-h-[260px] overflow-y-auto pr-3 custom-scrollbar">
                {taskEvents.map((ev) => (
                  <li
                    key={ev.id}
                    className="bg-red-950/40 p-4 rounded-xl border border-red-800/40 hover:bg-red-900/50 transition-all duration-200"
                  >
                    <p className="font-medium text-red-100 leading-tight">{ev.title}</p>
                    {ev.description && (
                      <p className="text-sm text-red-200/70 mt-2 line-clamp-2">
                        {ev.description}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Праздники */}
          <div className="bg-gradient-to-br from-green-950/80 to-emerald-950/80 border border-green-700/50 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-green-200">
                Праздники ({holidayEvents.length})
              </h3>
            </div>
            {holidayEvents.length === 0 ? (
              <p className="text-green-300/80 text-center py-6">Нет праздников на сегодня</p>
            ) : (
              <ul className="space-y-3 max-h-[260px] overflow-y-auto pr-3 custom-scrollbar">
                {holidayEvents.map((ev) => (
                  <li
                    key={ev.id}
                    className="bg-green-950/40 p-4 rounded-xl border border-green-800/40 hover:bg-green-900/50 transition-all duration-200"
                  >
                    <p className="font-medium text-green-100 leading-tight">{ev.title}</p>
                    {ev.description && (
                      <p className="text-sm text-green-200/70 mt-2 line-clamp-2">
                        {ev.description}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Встречи */}
          <div className="bg-gradient-to-br from-blue-950/80 to-cyan-950/80 border border-blue-700/50 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Users className="h-6 w-6 text-blue-400" />
              <h3 className="text-lg font-semibold text-blue-200">
                Встречи ({meetingEvents.length})
              </h3>
            </div>
            {meetingEvents.length === 0 ? (
              <p className="text-blue-300/80 text-center py-6">Нет встреч на сегодня</p>
            ) : (
              <ul className="space-y-3 max-h-[260px] overflow-y-auto pr-3 custom-scrollbar">
                {meetingEvents.map((ev) => (
                  <li
                    key={ev.id}
                    className="bg-blue-950/40 p-4 rounded-xl border border-blue-800/40 hover:bg-blue-900/50 transition-all duration-200"
                  >
                    <p className="font-medium text-blue-100 leading-tight">{ev.title}</p>
                    {ev.description && (
                      <p className="text-sm text-blue-200/70 mt-2 line-clamp-2">
                        {ev.description}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Локальное ЧС */}
          <div className="bg-gradient-to-br from-orange-950/80 to-amber-950/80 border border-orange-700/50 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-orange-400" />
              <h3 className="text-lg font-semibold text-orange-200">
                Локальное ЧС ({emergencyEvents.length})
              </h3>
            </div>
            {emergencyEvents.length === 0 ? (
              <p className="text-orange-300/80 text-center py-6">Нет локальных ЧС на сегодня</p>
            ) : (
              <ul className="space-y-3 max-h-[260px] overflow-y-auto pr-3 custom-scrollbar">
                {emergencyEvents.map((ev) => (
                  <li
                    key={ev.id}
                    className="bg-orange-950/40 p-4 rounded-xl border border-orange-800/40 hover:bg-orange-900/50 transition-all duration-200"
                  >
                    <p className="font-medium text-orange-100 leading-tight">{ev.title}</p>
                    {ev.description && (
                      <p className="text-sm text-orange-200/70 mt-2 line-clamp-2">
                        {ev.description}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      </CardContent>
    </Card>
  );
}